import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import * as Mui from "@mui/material";
import { useFetch } from "services/hooks/useFetch";
import { themes, scrollTop } from "services/constants";
import { showToast } from "services/utils/Status";
import { useSelector } from "react-redux";
import { CustomButton } from "components/CustomComponents/CustomButton";
import { TableEmptyState } from "components/CustomComponents/EmptyState";
import { checkEditorPermission } from "services/utils/HelperFunction";
import { Loader } from "components/Loader";
import { shiftCategoryHeader } from "pages/Administration/AdministrationHeaders";
import { tableRowSize } from "services/constants";
import { CreateShiftCategory } from "./CreateShiftCategory";
import { ViewSftCategory } from "./ViewShiftCategory";
import { OTbrands } from "./OTbrands";
import { CustomFilter } from "components/CustomComponents/CustomFilter";
import { Typography, Box, Paper, Drawer, Tooltip } from "@mui/material";
import { PeopleAlt } from "@mui/icons-material";
import { tooltipStyleProps } from "services/constants/Styles";
import { customGetAPI } from "services/utils/CustomAPI";
import TaskProgressCard from "components/CustomComponents/TaskProgressCard";
import moment from "moment";
import { useSaveView } from "components/CustomComponents/useSaveView";
import { toFlatDisplayHeaders } from "components/CustomComponents/headerGroupUtils";

export const ShiftCategory = ({ setSelectedShiftItem, subModuleId }) => {
  const { domain, userScreenAccess, topbarCompanyName, userDetails } =
    useSelector((state) => state.tokenReducer);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [sftCategoryDetails, setSftCategoryDetails] = useState([]);
  const [editCategoryDetails, setEditCategoryDetails] = useState({});
  const [hanldeLoader, setHandleLoader] = useState(true);
  const containerRef = useRef(null);

  const [taskProgress, setTaskProgress] = useState({
    active: false,
    progress: 0,
    message: "",
    status: null,
    title: "Shift Category Update in Progress",
    initiatedBy: "System",
    estimatedTime: "",
    shiftCategoryName: "",
    taskId: null,
    created_at: null,
    updated_at: null,
    duration: null,
    image_url: null,
  });

  let check_access = checkEditorPermission(
    userScreenAccess?.module_details,
    "Shift",
    "Shift Category",
  );

  const {
    result: shiftData,
    setLoad: reCallApi,
    loading,
  } = useFetch(
    `${domain}administration/shiftroster/shiftrota/shift-categories`,
  );

  const { result: complianceData } = useFetch(`${domain}compliance/`);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    scrollTop(containerRef);
  }, [page]);

  const [isCreatePanelOpen, setIsCreatePanelOpen] = useState(false);
  const [isViewPanelOpen, setIsViewPanelOpen] = useState(false);
  const [isOTBrandsPanelOpen, setIsOTBrandsPanelOpen] = useState(false);
  const pollingIntervalRef = useRef(null);
  const isBandAllowed = complianceData?.is_band_allowed === true;
  const paymentBasedOnColumnSx = {
    width: 160,
    minWidth: 160,
    maxWidth: 160,
    whiteSpace: "normal",
    wordBreak: "break-word",
  };

  const handleCreateShift = () => {
    setIsCreatePanelOpen(true);
  };

  const handleOpenOTBrands = () => {
    setIsOTBrandsPanelOpen(true);
  };

  const handleCloseOTBrands = () => {
    setIsOTBrandsPanelOpen(false);
  };

  const handleViewShift = () => {
    setIsViewPanelOpen(true);
  };

  const handleCreateClosePanel = () => {
    setIsCreatePanelOpen(false);
  };

  const handleViewClosePanel = () => {
    setIsViewPanelOpen(false);
  };

  const handleFilteredDataChange = useCallback(
    (filteredData) => {
      setSftCategoryDetails(filteredData);
      if (page * rowsPerPage >= filteredData.length) setPage(0);
    },
    [page, rowsPerPage],
  );

  const calculateRemainingTime = (progress) => {
    console.log(progress);
    if (progress === 0) return "Calculating...";
    if (progress >= 100) return "Completed";

    const elapsedMinutes = 2;
    const totalEstimatedMinutes = (elapsedMinutes * 100) / progress;
    const remainingMinutes = totalEstimatedMinutes - elapsedMinutes;

    if (remainingMinutes < 1) return "Less than 1 minute";
    if (remainingMinutes < 60) return `${Math.ceil(remainingMinutes)} mins`;

    const hours = Math.floor(remainingMinutes / 60);
    const minutes = Math.ceil(remainingMinutes % 60);
    return `${hours} hr ${minutes} mins`;
  };

  const pollTaskStatus = async (taskId) => {
    if (!taskId) return;

    try {
      const url = `${domain}background-tasks/?task_id=${taskId}`;
      const response = await customGetAPI(url);

      if (response.ok && response.responseDetails.data) {
        const data = response.responseDetails.data[0];
        console.log(data.result);
        const updatedProgress = {
          active: !data.is_completed,
          progress: data.progress || taskProgress.progress,
          message: data.message || taskProgress.message,
          status: data.status || taskProgress.status,
          title: "Shift Category Update in Progress",
          initiatedBy: data.createdby || taskProgress.initiatedBy || "System",
          estimatedTime: calculateRemainingTime(
            data.progress || taskProgress.progress,
          ),
          shiftCategoryName:
            data.result.shift_category_name ||
            taskProgress.shiftCategoryName ||
            "Unknown Category",
          taskId: taskId,
          created_at: data.created_at,
          updated_at: data.updated_at,
          duration: data.duration,
          image_url: data.image_url,
        };

        setTaskProgress(updatedProgress);

        if (data.is_completed) {
          if (data.status === "SUCCESS") {
            showToast("success", "Task completed.");
            reCallApi(true);
          }

          setTimeout(() => {
            setTaskProgress((prev) => ({
              ...prev,
              active: false,
              progress: 100,
              status: "SUCCESS",
            }));
          }, 3000);
        } else {
          setTimeout(() => pollTaskStatus(taskId), 5000);
        }
      }
    } catch (error) {
      console.error("Error polling task status:", error);
      setTimeout(() => pollTaskStatus(taskId), 10000);
    }
  };

  const checkActiveTasks = async () => {
    try {
      const url = `${domain}background-tasks/`;
      const response = await customGetAPI(url);

      if (!response?.ok) return;

      const rawData = response?.responseDetails?.data;
      const tasks = Array.isArray(rawData) ? rawData : [];

      if (!tasks.length) return;

      const activeTasks = tasks.filter(
        (task) =>
          !task.is_completed &&
          (task.status === "PROGRESS" || task.status === "PENDING"),
      );

      if (!activeTasks.length) return;

      const latestTask = activeTasks[0];
      const taskMetadata = latestTask.metadata || {};

      setTaskProgress({
        active: true,
        progress: latestTask.progress ?? 0,
        message: latestTask.message ?? "Processing update...",
        status: latestTask.status ?? "PROGRESS",
        title: "Shift Category Update in Progress",
        initiatedBy:
          latestTask.createdby ?? taskMetadata.initiated_by ?? "System",
        estimatedTime: calculateRemainingTime(latestTask.progress ?? 0),
        shiftCategoryName:
          latestTask.result.shift_category_name ??
          taskMetadata.shift_category_name ??
          "Unknown Category",
        taskId: latestTask.task_id,
        created_at: latestTask.created_at ?? null,
        updated_at: latestTask.updated_at ?? null,
        duration: latestTask.duration ?? null,
        image_url: latestTask.image_url ?? null,
      });

      pollTaskStatus(latestTask.task_id);
    } catch (error) {
      console.error("Error checking active tasks:", error);
    }
  };

  // ADD: Check for active tasks on component mount
  useEffect(() => {
    if (!isCreatePanelOpen && !isViewPanelOpen) {
      checkActiveTasks();
    }
  }, [domain, isCreatePanelOpen, isViewPanelOpen]);

  // ADD: Polling effect - checks every 5 seconds for background task updates
  useEffect(() => {
    // Clear any existing polling interval
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    // Set up polling interval
    pollingIntervalRef.current = setInterval(async () => {
      try {
        const url = `${domain}background-tasks/`;
        const response = await customGetAPI(url);

        if (!response?.ok) return;

        const rawData = response?.responseDetails?.data;
        const tasks = Array.isArray(rawData) ? rawData : [];

        if (!tasks.length) return;

        const activeTasks = tasks.filter(
          (task) =>
            !task.is_completed &&
            (task.status === "PROGRESS" || task.status === "PENDING"),
        );

        if (!activeTasks.length) {
          // No active tasks, ensure taskProgress is cleared
          if (taskProgress.active) {
            setTaskProgress((prev) => ({
              ...prev,
              active: false,
            }));
          }
          return;
        }

        // Active tasks detected
        const latestTask = activeTasks[0];
        const taskMetadata = latestTask.metadata || {};

        // Update task progress state
        setTaskProgress({
          active: true,
          progress: latestTask.progress ?? 0,
          message: latestTask.message ?? "Processing update...",
          status: latestTask.status ?? "PROGRESS",
          title: "Shift Category Update in Progress",
          initiatedBy:
            latestTask.createdby ?? taskMetadata.initiated_by ?? "System",
          estimatedTime: calculateRemainingTime(latestTask.progress ?? 0),
          shiftCategoryName:
            latestTask.result?.shift_category_name ??
            taskMetadata.shift_category_name ??
            "Unknown Category",
          taskId: latestTask.task_id,
          created_at: latestTask.created_at ?? null,
          updated_at: latestTask.updated_at ?? null,
          duration: latestTask.duration ?? null,
          image_url: latestTask.image_url ?? null,
        });

        // Auto-fallback to main component if any panel is open
        if (isCreatePanelOpen || isViewPanelOpen) {
          handleCreateClosePanel();
          handleViewClosePanel();
          showToast(
            "info",
            "Detected background task in progress. Returning to main view to display progress.",
          );
        }
      } catch (error) {
        console.error("Error in polling interval:", error);
      }
    }, 5000); // Poll every 5 seconds

    // Cleanup interval on unmount
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [domain, taskProgress.active, isCreatePanelOpen, isViewPanelOpen]);

  const reportMetadata = useMemo(
    () => ({
      "Company Name": topbarCompanyName || "N/A",
      "Report Generated On": moment().format("DD/MM/YYYY HH:mm:ss"),
    }),
    [topbarCompanyName],
  );

  const handleSavedViewChange = useCallback(() => {
    setPage(0);
  }, []);

  const { visibleHeaders, titleOverride, newButtonComponent } = useSaveView({
    domain,
    userId: userDetails?.id,
    subModuleId,
    storageKey: "shift_category_saved_views",
    fullHeaders: shiftCategoryHeader,
    screenAccess: check_access,
    onViewChange: handleSavedViewChange,
  });

  return hanldeLoader && loading ? (
    <Loader />
  ) : (
    <React.Fragment>
      <Box>
        <Box
          sx={{
            mb: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ mb: 2 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
              Shift Category
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Defines employee pay rules along with payroll applicability
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mr: 2.5 }}>
            {isBandAllowed && (
              <CustomButton
                actionFuntion={() => {
                  if (check_access && !taskProgress.active) {
                    handleOpenOTBrands();
                  } else if (!check_access) {
                    showToast(
                      "error",
                      `Access denied: You are not authorized to create shift`,
                    );
                  } else {
                    showToast(
                      "info",
                      "Please wait for the current update to complete",
                    );
                  }
                }}
                actionName="Overtime Bands"
                typeName="button"
                disabled={taskProgress.active}
              />
            )}
            <Tooltip
              title="Move to Shift Category Assignment"
              arrow
              slotProps={tooltipStyleProps}
            >
              <Mui.IconButton
                size="small"
                sx={{
                  bgcolor: themes?.whiteColor,
                  borderRadius: 1,
                  border: `1px solid ${themes?.mutedgreen}`,
                  width: 34,
                  height: 34,
                  mt: 0.5,
                  opacity: taskProgress.active ? 0.5 : 1,
                  transition: "opacity 0.3s ease",
                }}
                onClick={() => setSelectedShiftItem("Employee Shift Category")}
                disabled={taskProgress.active}
              >
                <PeopleAlt
                  sx={{ fontSize: 18, color: themes?.softMutedGreen }}
                />
              </Mui.IconButton>
            </Tooltip>
          </Box>
        </Box>

        {taskProgress.active && (
          <TaskProgressCard taskProgress={taskProgress} />
        )}

        <Paper sx={{ width: "100%", height: "77vh" }}>
          <CustomFilter
            title="Shift Category Records"
            titleOverride={titleOverride}
            filterHeaders={toFlatDisplayHeaders(visibleHeaders)}
            data={shiftData}
            filteredData={sftCategoryDetails}
            onFilteredDataChange={handleFilteredDataChange}
            screenAccess={check_access}
            downloadFormats={["excel", "pdf", "csv"]}
            includeMetadata={true}
            metadata={reportMetadata}
            newButtonComponent={
              <>
                {newButtonComponent}
                <Box
                  sx={{
                    opacity: taskProgress.active ? 0.5 : 1,
                    transition: "opacity 0.3s ease",
                  }}
                >
                  <CustomButton
                    actionFuntion={() => {
                      if (check_access && !taskProgress.active) {
                        handleCreateShift();
                      } else if (!check_access) {
                        showToast(
                          "error",
                          `Access denied: You are not authorized to create shift`,
                        );
                      } else {
                        showToast(
                          "info",
                          "Please wait for the current update to complete",
                        );
                      }
                    }}
                    actionName="New"
                    typeName="button"
                    disabled={taskProgress.active}
                  />
                </Box>
              </>
            }
          />

          <Mui.Grid sx={{ ml: 2, mr: 2 }}>
            <Mui.TableContainer ref={containerRef} sx={{ maxHeight: 590 }}>
              <Mui.Table
                className="border"
                sx={{
                  borderCollapse: "separate",
                  borderSpacing: 0,
                }}
              >
                <Mui.TableHead
                  sx={{
                    backgroundColor: themes.primary,
                    position: "sticky",
                    top: 0,
                    zIndex: 10,
                  }}
                >
                  <Mui.TableRow sx={tableRowSize}>
                    {visibleHeaders.map((header) => (
                      <Mui.TableCell
                        key={header.name}
                        align={
                          header.name === "Name" ||
                          header.name === "Payment Calculation based On"
                            ? "left"
                            : "center"
                        }
                        colSpan={header.children ? header.children.length : 1}
                        rowSpan={header.children ? 1 : 2}
                        sx={{
                          backgroundColor: themes.primary,
                          color: themes.headLine,
                          fontWeight: "bold",
                          border: "1px solid #ddd",
                          ...(header.key === "payment_based_on"
                            ? paymentBasedOnColumnSx
                            : {}),
                        }}
                      >
                        {header.name}
                      </Mui.TableCell>
                    ))}
                  </Mui.TableRow>

                  <Mui.TableRow sx={tableRowSize}>
                    {visibleHeaders
                      .filter((header) => header.children)
                      .flatMap((header) =>
                        header.children.map((child) => (
                          <Mui.TableCell
                            key={child.key}
                            align="center"
                            sx={{
                              backgroundColor: themes.primary,
                              color: themes.headLine,
                              fontWeight: "bold",
                              border: "1px solid #ddd",
                            }}
                          >
                            {child.name}
                          </Mui.TableCell>
                        )),
                      )}
                  </Mui.TableRow>
                </Mui.TableHead>
                <Mui.TableBody>
                  {sftCategoryDetails?.length === 0 ? (
                    <TableEmptyState dataLength={shiftData} />
                  ) : (
                    sftCategoryDetails
                      ?.slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage,
                      )
                      ?.map((row) => (
                        <Mui.TableRow sx={tableRowSize}>
                          {visibleHeaders.flatMap((header) =>
                            header.children
                              ? header.children.map((child) => (
                                  <Mui.TableCell
                                    key={child.key}
                                    align={
                                      header.key === "name" ||
                                      header.key === "payment_based_on"
                                        ? "left"
                                        : "center"
                                    }
                                    sx={{
                                      border: "1px solid #ddd",
                                      cursor: taskProgress.active
                                        ? "not-allowed"
                                        : "pointer",
                                      opacity: taskProgress.active ? 0.6 : 1,
                                      ...(child.key === "payment_based_on"
                                        ? paymentBasedOnColumnSx
                                        : {}),
                                    }}
                                    onClick={() => {
                                      if (!taskProgress.active) {
                                        handleViewShift();
                                        setEditCategoryDetails(row);
                                      } else {
                                        showToast(
                                          "info",
                                          "Please wait for the current update to complete",
                                        );
                                      }
                                    }}
                                  >
                                    {typeof row[child.key] === "boolean"
                                      ? row[child.key]
                                        ? "Enabled"
                                        : "Disabled"
                                      : (row[child.key] ?? "-")}
                                  </Mui.TableCell>
                                ))
                              : [
                                  <Mui.TableCell
                                    key={header.key}
                                    align={
                                      header.key === "name" ||
                                      header.key === "payment_based_on"
                                        ? "left"
                                        : "center"
                                    }
                                    onClick={() => {
                                      if (!taskProgress.active) {
                                        handleViewShift();
                                        setEditCategoryDetails(row);
                                      } else {
                                        showToast(
                                          "info",
                                          "Please wait for the current update to complete",
                                        );
                                      }
                                    }}
                                    sx={{
                                      border: "1px solid #ddd",
                                      cursor: taskProgress.active
                                        ? "not-allowed"
                                        : "pointer",
                                      opacity: taskProgress.active ? 0.6 : 1,
                                      ...(header.key === "payment_based_on"
                                        ? paymentBasedOnColumnSx
                                        : {}),
                                    }}
                                  >
                                    {typeof row[header.key] === "boolean"
                                      ? row[header.key]
                                        ? "Enabled"
                                        : "Disabled"
                                      : (row[header.key] ?? "-")}
                                  </Mui.TableCell>,
                                ],
                          )}
                        </Mui.TableRow>
                      ))
                  )}
                </Mui.TableBody>
              </Mui.Table>
            </Mui.TableContainer>
            <Box sx={{ mt: 1 }}>
              {sftCategoryDetails?.length > 25 && (
                <Mui.TablePagination
                  className="custom-pagination"
                  component="div"
                  rowsPerPageOptions={[25, 50, 75, 100]}
                  count={sftCategoryDetails?.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              )}
            </Box>
          </Mui.Grid>
        </Paper>
      </Box>

      <Drawer
        anchor="right"
        open={isCreatePanelOpen || isViewPanelOpen || isOTBrandsPanelOpen}
        onClose={(event, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") {
            return;
          }
          if (isCreatePanelOpen) {
            handleCreateClosePanel();
          } else if (isViewPanelOpen) {
            handleViewClosePanel();
          } else if (isOTBrandsPanelOpen) {
            handleCloseOTBrands();
          }
        }}
        sx={{
          "& .MuiDrawer-paper": {
            width: "680px",
            maxWidth: "90vw",
            backgroundColor: themes?.themes?.whiteColor,
          },
        }}
      >
        {isCreatePanelOpen ? (
          <CreateShiftCategory
            onClose={handleCreateClosePanel}
            reCallApi={reCallApi}
            setHandleLoader={setHandleLoader}
            complianceData={complianceData}
          />
        ) : isViewPanelOpen ? (
          <ViewSftCategory
            onClose={handleViewClosePanel}
            editCategoryDetails={editCategoryDetails}
            reCallApi={reCallApi}
            setHandleLoader={setHandleLoader}
            screenAccess={check_access}
            complianceData={complianceData}
            setIsViewPanelOpen={setIsViewPanelOpen}
            // ADD THESE NEW PROPS
            onTaskStart={(taskData) => {
              setTaskProgress({
                active: true,
                progress: 0,
                message: taskData.message || "Starting update...",
                status: "PENDING",
                title: "Shift Category Update in Progress",
                initiatedBy: "User",
                estimatedTime: calculateRemainingTime(0),
                shiftCategoryName:
                  taskData.categoryName || editCategoryDetails?.name,
                taskId: taskData.taskId,
                created_at: taskData.created_at ?? null,
                updated_at: taskData.updated_at ?? null,
                duration: taskData.duration ?? null,
                image_url: taskData.image_url ?? null,
              });

              if (taskData.taskId) {
                setTimeout(() => pollTaskStatus(taskData.taskId), 2000);
              }
            }}
            onTaskUpdate={(updates) => {
              const updatedProgress = {
                ...taskProgress,
                ...updates,
                estimatedTime: calculateRemainingTime(
                  updates.progress || taskProgress.progress,
                ),
              };

              setTaskProgress(updatedProgress);
            }}
            onTaskComplete={() => {
              setTaskProgress({
                active: false,
                progress: 0,
                message: "",
                status: null,
                title: "Shift Category Update in Progress",
                initiatedBy: "",
                estimatedTime: "",
                shiftCategoryName: "",
                taskId: null,
                created_at: null,
                updated_at: null,
                duration: null,
                image_url: null,
              });

              if (isViewPanelOpen) {
                handleViewClosePanel();
              }

              reCallApi(true);
            }}
          />
        ) : isOTBrandsPanelOpen ? (
          <OTbrands onClose={handleCloseOTBrands} />
        ) : null}
      </Drawer>
    </React.Fragment>
  );
};
